<template>
  <div class="login-container clipr-theme">
    <div class="login-left">
      <div class="login-content">
        <div class="logo-section">
          <img src="/favicon.svg" alt="Clipr" class="logo-image" />
          <span class="logo-text">Clipr</span>
        </div>

        <div class="welcome-section">
          <h1 class="welcome-title">{{ $t("updatePassword.title") }}</h1>
          <p class="welcome-description">
            {{ $t("updatePassword.description") }}
          </p>
        </div>

        <form @submit.prevent="handleUpdatePassword" class="login-form">
          <div class="form-group compact-group">
            <label for="update-password">{{
              $t("updatePassword.newPasswordLabel")
            }}</label>
            <div class="input-wrapper">
              <i
                :class="['input-icon', showPassword ? 'ti-unlock' : 'ti-lock']"
              ></i>
              <i
                class="ti-eye input-icon input-icon-right"
                @click="showPassword = !showPassword"
                style="cursor: pointer;"
              ></i>
              <input
                id="update-password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                :placeholder="$t('updatePassword.newPasswordPlaceholder')"
                required
                class="form-input has-right-icon"
              />
            </div>
            <ul class="password-requirements" v-if="password">
              <li
                v-for="(req, index) in passwordRequirements"
                :key="index"
                :class="{ 'met': req.met }"
              >
                <i :class="['req-icon', req.met ? 'ti-check' : 'ti-close']"></i>
                {{ req.label }}
              </li>
            </ul>
          </div>

          <div class="form-group compact-group">
            <label for="update-confirm">{{ $t("login.confirmPasswordLabel") }}</label>
            <div class="input-wrapper">
              <i
                :class="['input-icon', showConfirmPassword ? 'ti-unlock' : 'ti-lock']"
              ></i>
              <i
                class="ti-eye input-icon input-icon-right"
                @click="showConfirmPassword = !showConfirmPassword"
                style="cursor: pointer;"
              ></i>
              <input
                id="update-confirm"
                :type="showConfirmPassword ? 'text' : 'password'"
                v-model="confirmPassword"
                :placeholder="$t('login.placeholderRepeatPassword')"
                required
                class="form-input has-right-icon"
              />
            </div>
          </div>

          <div v-if="error" class="error-message">
            {{ error }}
          </div>
          <div v-if="success" class="success-message">
            {{ success }}
          </div>

          <button
            type="submit"
            class="btn-signin"
            :disabled="loading"
          >
            {{
              loading ? $t("updatePassword.updating") : $t("updatePassword.submit")
            }}
          </button>

          <div class="signup-link">
            <a href="/login" @click.prevent="handleBackToLogin">{{ $t("login.backToLogin") }}</a>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase";

export default {
  name: "UpdatePassword",
  data() {
    return {
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      error: null,
      success: null,
    };
  },
  computed: {
    passwordRequirements() {
      void this.$i18n.locale;
      const password = this.password;
      const t = (key) =>
        typeof this.$t === "function" ? this.$t(key) : key;
      return [
        { label: t("login.reqMinChars"), met: password.length >= 8 },
        { label: t("login.reqUppercase"), met: /[A-Z]/.test(password) },
        { label: t("login.reqNumber"), met: /[0-9]/.test(password) },
        {
          label: t("login.reqSpecial"),
          met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        },
      ];
    },
    isPasswordValid() {
      return this.passwordRequirements.every(req => req.met);
    }
  },
  methods: {
    async handleUpdatePassword() {
      if (!this.password || !this.confirmPassword) {
        this.error = this.$t("login.signupAllFieldsRequired");
        return;
      }

      if (!this.isPasswordValid) {
        this.error = this.$t("login.signupPasswordRequirements");
        return;
      }

      if (this.password !== this.confirmPassword) {
        this.error = this.$t("login.signupPasswordsMismatch");
        return;
      }

      try {
        this.loading = true;
        this.error = null;
        this.success = null;

        const { error } = await supabase.auth.updateUser({
          password: this.password,
        });

        if (error) throw error;

        this.success = this.$t("updatePassword.success");
        setTimeout(() => {
          this.handleBackToLogin();
        }, 2000);
      } catch (error) {
        this.error = error.message || this.$t("updatePassword.errorGeneric");
      } finally {
        this.loading = false;
      }
    },
    async handleBackToLogin() {
      try {
        const { error } = await supabase.auth.signOut({ scope: "local" });
        if (error) throw error;
        if (this.$route.path !== "/login") {
          this.$router.replace({ path: "/login", query: { redirect: "/create-clips" } });
        }
      } catch (error) {
        this.error = error.message || this.$t("updatePassword.errorGeneric");
      }
    }
  },
};
</script>

<style scoped>
.login-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  color: var(--clipr-text-primary);
}

.login-container.clipr-theme {
  background: radial-gradient(circle at top, rgba(59, 130, 246, 0.16) 0%, transparent 55%),
    radial-gradient(circle at bottom, rgba(168, 85, 247, 0.16) 0%, transparent 55%),
    var(--clipr-bg-primary);
}

.login-container.clipr-theme .login-content {
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.7), 0 0 60px rgba(59, 130, 246, 0.3);
  border-color: var(--clipr-border-color);
}

.login-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.login-content {
  max-width: 460px;
  width: 100%;
  padding: 32px 32px 36px;
  border-radius: var(--clipr-radius-2xl);
  background: var(--clipr-bg-card-glass);
  border: 1px solid var(--clipr-border-color);
  backdrop-filter: blur(24px);
  animation: clipr-float 10s ease-in-out infinite;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}

.logo-image {
  width: 40px;
  height: 40px;
  display: block;
  object-fit: contain;
}

.logo-text {
  color: var(--clipr-text-primary);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.welcome-section {
  margin-bottom: 28px;
}

.welcome-title {
  font-size: clamp(28px, 3vw, 32px);
  font-weight: 600;
  color: var(--clipr-text-primary);
  margin: 0 0 10px 0;
  letter-spacing: -0.02em;
}

.welcome-description {
  font-size: 15px;
  color: var(--clipr-text-secondary);
  line-height: 1.4;
  margin: 0;
}

.login-form { width: 100%; }

.form-group { margin-bottom: 22px; }
.compact-group { margin-bottom: 12px; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--clipr-text-secondary);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.input-wrapper { position: relative; display: flex; align-items: center; }

.input-icon {
  position: absolute;
  left: 16px;
  color: var(--clipr-text-tertiary);
  font-size: 18px;
  z-index: 1;
}

.input-icon-right {
  right: 16px;
  left: auto;
}

.form-input {
  width: 100%;
  padding: 12px 16px 12px 46px;
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-lg);
  font-size: 15px;
  color: var(--clipr-text-primary);
  transition: var(--clipr-transition);
  background-color: rgba(15, 15, 18, 0.9);
}

.form-input.has-right-icon {
  padding-right: 46px;
}

.form-input:focus {
  outline: none;
  border-color: var(--clipr-border-purple);
  box-shadow: 0 0 0 3px var(--clipr-purple-glow);
}

.form-input::placeholder { color: var(--clipr-text-tertiary); }

.error-message {
  background-color: rgba(248, 113, 113, 0.12);
  color: #fecaca;
  border-radius: var(--clipr-radius-md);
  font-size: 13px;
  padding: 10px 12px;
  margin-bottom: 18px;
  border: 1px solid rgba(248, 113, 113, 0.35);
}

.success-message {
  background-color: rgba(34, 197, 94, 0.15);
  color: #bbf7d0;
  border-radius: var(--clipr-radius-md);
  font-size: 13px;
  padding: 10px 12px;
  margin-bottom: 18px;
  border: 1px solid rgba(34, 197, 94, 0.4);
}

.btn-signin {
  width: 100%;
  padding: 13px;
  border-radius: var(--clipr-radius-lg);
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 22px;
  background: var(--clipr-purple-gradient);
  color: #fff;
  box-shadow: 0 8px 30px var(--clipr-purple-glow);
  transition: var(--clipr-transition);
}

.btn-signin:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 38px rgba(168, 85, 247, 0.35);
}

.btn-signin:disabled { opacity: 0.6; cursor: not-allowed; }

.signup-link { text-align: center; font-size: 13px; color: var(--clipr-text-tertiary); }
.signup-link a { color: var(--clipr-purple-light); text-decoration: none; font-weight: 600; }
.signup-link a:hover { text-decoration: underline; }

.password-requirements {
  list-style: none;
  padding: 0;
  margin: 10px 0 0 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 14px;
  row-gap: 8px;
}

.password-requirements li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 11px;
  color: var(--clipr-text-tertiary);
  transition: color 0.2s;
  line-height: 1.35;
}

.password-requirements li.met {
  color: var(--clipr-text-secondary);
  font-weight: 600;
}

.req-icon { font-size: 10px; color: #f97373; }
.password-requirements li.met .req-icon { color: #4ade80; }

@media (max-width: 968px) {
  .login-container { flex-direction: column; }
  .login-left { padding: 30px 20px; }
  .login-content { box-shadow: 0 28px 60px rgba(0, 0, 0, 0.7); }
}

@media (max-width: 480px) {
  .welcome-title { font-size: 26px; }
  .login-content {
    padding: 24px 22px 26px;
    border-radius: var(--clipr-radius-xl);
  }

  .password-requirements {
    grid-template-columns: 1fr;
  }
}

@keyframes clipr-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-4px);
  }
}
</style>

